import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  Alert,
  Paper,
  Text,
  Modal,
  RowActions,
  ProductImageAvatar,
  ProductImageCarousel,
  MobileListRow,
  type ResourceQuery,
} from '@vipso/shared';
import { useListProductsQuery, type Product } from '../api/tenantApi';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { useModuleEntitled, useModuleLabel, useModuleTerminology } from '../hooks/useModuleLabel';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import type { RootState } from '../store';
import {
  downloadProductsImportTemplate,
  importProductsExcel,
} from '../api/importProducts';
import { useFileViewer } from '../features/files/FileViewerContext';

export function ProductsPage() {
  const { t } = useTranslation();
  const { openFileViewer } = useFileViewer();
  const navigate = useNavigate();
  const access = useModuleAccess('product');
  const approvalAccess = useApprovalModuleAccess();
  const moduleLabel = useModuleLabel('product', 'products.title');
  const terms = useModuleTerminology('product');
  const entitled = useModuleEntitled('product');
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });

  const { data, isLoading, isFetching, isError, refetch } = useListProductsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    parsed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const list = data?.listProducts
    ? {
        rows: (data.listProducts.rows ?? []) as Product[],
        total: data.listProducts.total ?? 0,
        page: query.page,
        pageLimit: query.pageLimit,
      }
    : undefined;

  async function handleDownloadTemplate() {
    if (!accessToken) return;
    setTemplateLoading(true);
    setImportError(null);
    try {
      await downloadProductsImportTemplate(accessToken);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('products.importFailed'));
    } finally {
      setTemplateLoading(false);
    }
  }

  async function handleImportUpload() {
    if (!accessToken || !importFile) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await importProductsExcel(importFile, accessToken);
      setImportResult({
        imported: result.imported,
        parsed: result.parsed,
        errors: result.errors,
      });
      setImportFile(null);
      if (result.imported > 0) void refetch();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('products.importFailed'));
    } finally {
      setImporting(false);
    }
  }

  if (!entitled) return <Navigate to="/dashboard" replace />;

  return (
    <Stack gap="md">
      <PageHeader
        title={moduleLabel}
        description={t('terminology.manageRecords', { entity: terms.plural })}
        actions={
          access.canCreate ? (
            <Group gap="sm">
              <Button
                variant="default"
                disabled={!accessToken}
                onClick={() => {
                  setImportOpen(true);
                  setImportResult(null);
                  setImportError(null);
                  setImportFile(null);
                }}
              >
                {t('products.import')}
              </Button>
              <Button testId="product-add" onClick={() => navigate('/products/new')}>
                {t('terminology.add', { entity: terms.singular })}
              </Button>
            </Group>
          ) : undefined
        }
      />
      {isError ? <Alert color="red" title={t('terminology.loadFailed', { entity: terms.plural })} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('terminology.search', { entity: terms.plural })}
        emptyTitle={t('terminology.emptyTitle', { entity: terms.plural })}
        emptyDescription={t('terminology.emptyDescription', { entity: terms.singular })}
        onRefresh={() => void refetch()}
        refreshLabel={t('common.refresh')}
        testId="product-table"
        mobileView="list"
        onRowClick={(row) => navigate(`/products/${row.id}`)}
        rowActionsHeader={t('common.actions')}
        renderRowActions={
          access.canUpdate
            ? (row) => (
                <RowActions
                  editLabel={t('common.edit')}
                  onEdit={() => navigate(`/products/${row.id}/edit`)}
                />
              )
            : undefined
        }
        columns={[
          {
            key: 'image',
            header: t('products.image'),
            width: 56,
            render: (row) => (
              <ProductImageAvatar
                src={row.imageUrl}
                name={row.name}
                size={40}
                onClick={
                  row.imageFileId || row.imageUrl
                    ? (e) => {
                        e.stopPropagation();
                        if (row.imageFileId) {
                          openFileViewer({ fileId: row.imageFileId });
                        } else if (row.imageUrl) {
                          openFileViewer({ url: row.imageUrl });
                        }
                      }
                    : undefined
                }
              />
            ),
          },
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'unit', header: t('products.unit'), render: (row) => row.unit || t('common.emptyValue') },
          {
            key: 'category',
            header: t('products.category'),
            render: (row) => row.category?.name || t('common.emptyValue'),
          },
          ...(approvalAccess.product
            ? [
                {
                  key: 'approval',
                  header: t('approval.status'),
                  render: (row: Product) => (
                    <ApprovalStatusBadge status={row.approvalStatus} />
                  ),
                },
              ]
            : []),
          {
            key: 'price',
            header: t('products.price'),
            render: (row) =>
              row.pricePerQuantity != null
                ? `₹${Number(row.pricePerQuantity).toFixed(2)}`
                : t('common.emptyValue'),
          },
        ]}
        renderListItem={(row: Product) => (
          <MobileListRow
            title={row.name}
            subtitle={[row.unit, row.category?.name].filter(Boolean).join(' · ') || undefined}
            imageUrl={row.imageUrl}
            initialsFrom={row.name}
            trailing={
              access.canUpdate ? (
                <RowActions
                  editLabel={t('common.edit')}
                  onEdit={() => navigate(`/products/${row.id}/edit`)}
                />
              ) : undefined
            }
            onClick={() => navigate(`/products/${row.id}`)}
          />
        )}
        renderCard={(row: Product) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <ProductImageCarousel
                name={row.name}
                fallbackUrl={row.imageUrl}
                compact
                images={
                  row.imageUrl
                    ? [{ url: row.imageUrl, fileId: row.imageFileId ?? undefined, isMain: true }]
                    : []
                }
                onImageClick={(image) => {
                  if (image.fileId) {
                    openFileViewer({ fileId: image.fileId });
                  } else if (image.url) {
                    openFileViewer({ url: image.url });
                  }
                }}
              />
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="sm">
                    <Text fw={600}>{row.name}</Text>
                    {approvalAccess.product ? (
                      <ApprovalStatusBadge status={row.approvalStatus} />
                    ) : null}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {[row.unit, row.category?.name].filter(Boolean).join(' · ') || t('common.emptyValue')}
                  </Text>
                  {row.pricePerQuantity != null ? (
                    <Text size="sm" fw={600}>
                      ₹{Number(row.pricePerQuantity).toFixed(2)}
                    </Text>
                  ) : null}
                </Stack>
                {access.canUpdate ? (
                  <RowActions
                    editLabel={t('common.edit')}
                    onEdit={() => navigate(`/products/${row.id}/edit`)}
                  />
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
      />

      <Modal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('products.importTitle')}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('products.importHint')}
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              loading={templateLoading}
              onClick={() => void handleDownloadTemplate()}
            >
              {t('products.downloadSample')}
            </Button>
          </Group>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setImportFile(e.target.files?.[0] ?? null);
              setImportResult(null);
              setImportError(null);
            }}
          />
          {importFile ? (
            <Text size="sm">{t('products.selectedFile', { name: importFile.name })}</Text>
          ) : null}
          {importError ? <Alert color="red" title={importError} /> : null}
          {importResult ? (
            <Alert
              color={importResult.errors.length ? 'orange' : 'green'}
              title={t('products.importSummary', {
                imported: importResult.imported,
                parsed: importResult.parsed,
              })}
            >
              {importResult.errors.length ? (
                <Stack gap={4} mt="xs">
                  {importResult.errors.slice(0, 20).map((err) => (
                    <Text key={`${err.row}-${err.message}`} size="xs">
                      {t('products.importRowError', { row: err.row, message: err.message })}
                    </Text>
                  ))}
                  {importResult.errors.length > 20 ? (
                    <Text size="xs" c="dimmed">
                      {t('products.importMoreErrors', {
                        count: importResult.errors.length - 20,
                      })}
                    </Text>
                  ) : null}
                </Stack>
              ) : null}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setImportOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={importing}
              disabled={!importFile || !accessToken}
              onClick={() => void handleImportUpload()}
            >
              {t('products.uploadImport')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
